/**
 * The stand-in tree of the stylized look: a trunk and a round crown in the art's colours and about
 * its size, drawn until the art (trees.glb) has loaded, and for any kind that fails its checks.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const BARK = new THREE.Color(0x6b4a33);
const LEAF_LOW = new THREE.Color(0x1f4a2c);
const LEAF_HIGH = new THREE.Color(0x6aa457);

/** `geometry` with a colour per vertex, picked by its height. */
function painted(
  geometry: THREE.BufferGeometry,
  paint: (y: number, out: THREE.Color) => THREE.Color,
): THREE.BufferGeometry {
  const position = geometry.getAttribute('position');
  const colors = new Float32Array(position.count * 3);
  const c = new THREE.Color();
  for (let i = 0; i < position.count; i++) paint(position.getY(i), c).toArray(colors, i * 3);
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.deleteAttribute('uv');
  return geometry;
}

export function standInTree(): THREE.BufferGeometry {
  const trunk = painted(
    new THREE.CylinderGeometry(0.045, 0.065, 0.42, 6).translate(0, 0.19, 0),
    (_y, c) => c.copy(BARK),
  );
  const crown = painted(
    new THREE.SphereGeometry(0.29, 10, 8).scale(1, 0.88, 1).translate(0, 0.6, 0),
    (y, c) => c.lerpColors(LEAF_LOW, LEAF_HIGH, Math.min(1, Math.max(0, (y - 0.35) / 0.5))),
  );
  const tree = mergeGeometries([trunk, crown]);
  trunk.dispose();
  crown.dispose();
  return tree;
}
